import type { AppSettings } from '../appSettings';

export const DEFAULT_ENTRA_REDIRECT_PATH = '/sso';
export const NO_ACCESS_PATH = '/no-access';

/** Placeholder for the user segment of /login/:controlId/:userGuid/P; identity comes from MSAL only. */
export const ENTRA_LOGIN_USER_SEGMENT = 'entra';

export interface EntraSettings {
  clientId: string;
  authority: string;
  apiScope: string;
  redirectPath: string;
  redirectUri: string;
}

export type EntraSettingsResult =
  | { settings: EntraSettings; error?: undefined }
  | { settings?: undefined; error: string };

export function resolveEntraSettings(settings: AppSettings, origin: string): EntraSettingsResult {
  const clientId = (settings.entraClientId ?? '').trim();
  const authority = (settings.entraAuthority ?? '').trim();
  const apiScope = (settings.entraApiScope ?? '').trim();
  const redirectPath = (settings.entraRedirectPath ?? '').trim() || DEFAULT_ENTRA_REDIRECT_PATH;

  const missing = [
    clientId ? '' : 'entraClientId',
    authority ? '' : 'entraAuthority',
    apiScope ? '' : 'entraApiScope',
  ].filter(name => name);
  if (missing.length > 0) {
    return { error: `Missing ${missing.join(', ')}` };
  }
  if (!/^https:\/\/[^/?#]+\/[^?#]+$/i.test(authority)) {
    return { error: 'entraAuthority must be an absolute https URL that includes the tenant' };
  }
  if (!isSafeAppPath(redirectPath)) {
    return { error: 'entraRedirectPath must be a same-origin absolute path' };
  }

  return {
    settings: {
      clientId,
      authority,
      apiScope,
      redirectPath,
      redirectUri: origin + redirectPath,
    },
  };
}

/** True for an absolute in-app path such as "/sso"; rejects protocol-relative and backslash forms. */
export function isSafeAppPath(path: string): boolean {
  return /^\/(?![/\\])[^\\]*$/.test(path) && !/[\u0000-\u001f]/.test(path);
}

export function isSameAppPath(left: string, right: string): boolean {
  const normalize = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path).toLowerCase();
  return normalize(left) === normalize(right);
}

/**
 * Converts an in-app return URL into an absolute same-origin URL, or null when it points elsewhere.
 * Used for MSAL return targets so a crafted value cannot redirect the user off-site or back to the bridge.
 */
export function toSameOriginUrl(url: string | null | undefined, origin: string, excludedPaths: string[] = []): string | null {
  if (!url) {
    return null;
  }
  let target: URL;
  try {
    target = new URL(url, origin + '/');
  } catch {
    return null;
  }
  if (target.origin !== origin || !isSafeAppPath(target.pathname)) {
    return null;
  }
  if (excludedPaths.some(path => isSameAppPath(target.pathname, path))) {
    return null;
  }
  return target.origin + target.pathname + target.search + target.hash;
}

/** True when the request URL is on the API origin and under the API base path (never the SPA's own assets). */
export function isApiRequestUrl(url: string, apiBaseUrl: string, documentBaseUrl: string): boolean {
  if (!url || !apiBaseUrl) {
    return false;
  }
  let base: URL;
  let target: URL;
  let documentBase: URL;
  try {
    base = new URL(apiBaseUrl, documentBaseUrl);
    target = new URL(url, documentBaseUrl);
    documentBase = new URL(documentBaseUrl);
  } catch {
    return false;
  }
  if (target.origin !== base.origin) {
    return false;
  }
  if (target.origin === documentBase.origin && target.pathname.startsWith(new URL('assets/', documentBase).pathname)) {
    return false;
  }
  const basePath = base.pathname.endsWith('/') ? base.pathname : base.pathname + '/';
  return target.pathname.startsWith(basePath) || target.pathname === base.pathname;
}

/** True when the URL carries an authorization response (code or error) together with a state value. */
export function hasAuthResponseInUrl(location: Pick<Location, 'hash' | 'search'>): boolean {
  const containsResponse = (value: string): boolean => {
    if (!value || value.length < 2) {
      return false;
    }
    const params = new URLSearchParams(value.substring(1));
    return params.has('state') && (params.has('code') || params.has('error'));
  };
  return containsResponse(location.hash) || containsResponse(location.search);
}

/** True inside a frame or a window opened by another window (MSAL hidden iframe or popup). */
export function isChildBrowsingContext(): boolean {
  try {
    if (window.parent !== window) {
      return true;
    }
  } catch {
    return true;
  }
  try {
    return !!window.opener && window.opener !== window;
  } catch {
    return true;
  }
}
